import React, { createContext, useContext, useState, useEffect } from 'react';
import tokenService from '../services/tokenService';
import * as api from '../services/api';

export interface AuthContextType {
  isAuthenticated: boolean;
  token: string | null;
  email: string | null;
  user: api.User | null;
  login: (token: string, email: string) => void;
  logout: () => void;
  setUser: (user: api.User | null) => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper function for safely decoding JWT
const parseJwt = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return {};
    
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const paddedBase64 = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');
    
    const jsonPayload = decodeURIComponent(
      atob(paddedBase64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Error parsing JWT token:', e);
    return {};
  }
};

export const AuthProvider: React.FC<{children: React.ReactNode}> = ({ children }): React.ReactElement => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [user, setUser] = useState<api.User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [initialized, setInitialized] = useState(false);

  // Initialize auth state from storage on mount
  useEffect(() => {
    console.log('Auth initialization starting...');
    
    // Check for token in storage
    const storedToken = tokenService.getToken();
    
    if (tokenService.hasValidToken()) {
      console.log('Valid token found - setting authenticated state');
      
      // Update state with token
      setToken(storedToken);
      setIsAuthenticated(true);
      
      // Extract email from token
      try {
        const payload = parseJwt(storedToken!);
        if (payload.email) {
          setEmail(payload.email);
          
          // Initialize user data
          setLoading(true);
          api.initializeUser(payload.email)
            .then(userData => {
              console.log('User initialized successfully:', userData);
              setUser(userData);
            })
            .catch(error => {
              console.error('Failed to initialize user:', error);
              if (error.response?.status === 401) {
                // Token is invalid, clear it
                tokenService.removeToken();
                setIsAuthenticated(false);
                setToken(null);
                setEmail(null);
              }
            })
            .finally(() => {
              setLoading(false);
            });
        }
      } catch (error) {
        console.error('Error parsing token:', error);
        setLoading(false);
      }
    } else {
      console.log('No valid token found - starting unauthenticated');
      tokenService.removeToken(); // Clean up any invalid tokens
      setLoading(false);
    }
    
    setInitialized(true);
    console.log('Auth initialization complete');
  }, []);

  const login = (newToken: string, userEmail: string): void => {
    console.log('🔑 [AuthContext] Login called with:', {
      hasToken: !!newToken,
      tokenLength: newToken?.length,
      email: userEmail
    });
    
    if (!newToken) {
      console.error('🔑 [AuthContext] Invalid token provided to login');
      return;
    }

    try {
      console.log('🔑 [AuthContext] Storing token...');
      // Store token
      tokenService.setToken(newToken);

      // Verify storage immediately after setting
      const verifyToken = tokenService.getToken();
      console.log('🔑 [AuthContext] Token storage verification:', {
        stored: !!verifyToken,
        matches: verifyToken === newToken,
        length: verifyToken?.length
      });

      // Update state
      console.log('🔑 [AuthContext] Updating state...');
      setToken(newToken);
      setEmail(userEmail);
      setIsAuthenticated(true);
      
      // Initialize user data
      setLoading(true);
      api.initializeUser(userEmail)
        .then(userData => {
          console.log('User initialized successfully on login:', userData);
          setUser(userData);
        })
        .catch(error => {
          console.error('Failed to initialize user on login:', error);
        })
        .finally(() => {
          setLoading(false);
        });

      console.log('🔑 [AuthContext] State updated:', {
        isAuthenticated: true,
        hasToken: !!newToken,
        hasEmail: !!userEmail
      });
    } catch (error) {
      console.error('🔑 [AuthContext] Error during login:', error);
      setLoading(false);
    }
  };

  const logout = (): void => {
    // Remove token from storage
    tokenService.removeToken();
    
    // Update state
    setToken(null);
    setEmail(null);
    setUser(null);
    setIsAuthenticated(false);
    
    console.log('Logout successful - auth state cleared');
    
    // Redirect to home
    window.location.href = '/';
  };

  // Don't render children until auth is initialized
  if (!initialized) {
    return <div>Initializing application...</div>;
  }

  const value = {
    isAuthenticated,
    token,
    email,
    user,
    setUser,
    login,
    logout,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 